import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2 } from "lucide-react";

const ALL_ROLES = ["super_admin", "admin", "role_head", "member", "employee", "internal_committee"];
const label = (r) => r.replace(/_/g, " ");
const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong"; };
const EMPTY = { name: "", email: "", role: "member", password: "" };

function AddUserForm({ roles, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/users", f); toast.success(`Created ${f.email}`); setF(EMPTY); onCreated(); }
    catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr_auto_1fr_auto] gap-2 mb-5" data-testid="add-user-form">
      <input required className="tt-input text-sm" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-user-name" />
      <input required type="email" className="tt-input text-sm" placeholder="email@thickandthin.media" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-user-email" />
      <select className="tt-input text-sm md:!w-auto" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="add-user-role">
        {roles.map((r) => <option key={r} value={r}>{label(r)}</option>)}
      </select>
      <input required minLength={8} type="text" autoComplete="new-password" className="tt-input text-sm" placeholder="Temp password (8+ chars)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="add-user-password" />
      <button type="submit" disabled={busy} className="tt-btn-primary text-sm flex items-center justify-center gap-1.5" data-testid="add-user-submit"><UserPlus size={14} /> Add user</button>
    </form>
  );
}

export function UsersPanel({ me }) {
  const [users, setUsers] = useState([]);
  const isSuper = me?.role === "super_admin";
  const roles = isSuper ? ALL_ROLES : ALL_ROLES.filter((r) => !["super_admin", "admin"].includes(r));
  const canManage = (u) => isSuper || !["super_admin", "admin"].includes(u.role);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => { try { await api.patch(`/users/${u.user_id}`, { role, role_scopes: [] }); toast.success("Role updated"); load(); } catch (e) { toast.error(errText(e)); } };
  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (8+ characters)`);
    if (!pw) return;
    try { await api.post(`/users/${u.user_id}/password`, { password: pw }); toast.success("Password updated"); load(); } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will be signed out and can't log in.`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("User removed"); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <section className="tt-card p-6" data-testid="users-panel">
      <div className="tt-label mb-1">Users</div>
      <p className="text-xs text-[#71717A] mb-4">Only people added here can sign in. Share the temporary password with them directly.</p>
      <AddUserForm roles={roles} onCreated={load} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[#71717A] text-xs font-mono"><th className="text-left pb-2">Name</th><th className="text-left pb-2">Email</th><th className="text-left pb-2">Role</th><th className="text-left pb-2">Password</th><th /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-[#1E1E1E]" data-testid={`user-row-${u.email}`}>
                <td className="py-2 pr-3">{u.name}</td>
                <td className="py-2 pr-3 font-mono text-xs">{u.email}</td>
                <td className="py-2 pr-3">
                  {isSuper && u.user_id !== me.user_id ? (
                    <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className="tt-input !py-1 text-xs !w-auto" data-testid={`user-role-${u.email}`}>
                      {ALL_ROLES.map((r) => <option key={r} value={r}>{label(r)}</option>)}
                    </select>
                  ) : <span className="tt-badge-neutral">{label(u.role)}</span>}
                </td>
                <td className="py-2 pr-3">{u.has_password ? <span className="tt-badge-pass">set</span> : <span className="tt-badge-warn">Google only</span>}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  {(canManage(u) || u.user_id === me.user_id) && <button onClick={() => resetPw(u)} className="tt-btn-ghost p-1.5" title="Set password" data-testid={`user-reset-pw-${u.email}`}><KeyRound size={14} /></button>}
                  {canManage(u) && u.user_id !== me.user_id && <button onClick={() => remove(u)} className="tt-btn-ghost p-1.5 text-[#EF4444]" title="Remove" data-testid={`user-remove-${u.email}`}><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
